import React, { useContext, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import * as Yup from "yup";
import { FieldArray, Formik } from "formik";

import ImageUploader, {
  uploadBas64ToS3,
} from "../../components/utilities/ImageUploader";

import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { postNotification } from "../../components/utilities/Notifications";
import { getData, postData } from "../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../global/environmentVariables";

export default function StoreAdminProduct() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  let { productId } = useParams();

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [product, setProduct] = useState(null);

  const [saveBoxRef, saveBoxInView] = useInView({
    threshold: 0,
  });

  const handleCategoryChange = (e, setFieldValue, next) => {
    var categories = [];
    var addCommaBack =
      e.target.value.length > 0 &&
      e.target.value.charAt(e.target.value.length - 1) === ",";
    var cats = e.target.value.split(",");
    var catList = "";
    cats.forEach((cat) => {
      if (cat !== "") {
        categories.push(cat.trim().toLowerCase().split(" ").join("-"));
        if (catList !== "") {
          catList += ",";
        }
        catList += cat;
      }
    });
    if (addCommaBack) {
      catList += ",";
    }
    e.target.value = catList;
    next(e);
    setFieldValue("categories", categories);
  };

  function handleDeleteCategory(category, categories, setFieldValue) {
    var newCategories = [];
    var catList = "";
    categories.forEach((catKey) => {
      if (catKey !== category) {
        newCategories.push(catKey);
        if (catList !== "") {
          catList += ",";
        }
        catList += catKey;
      }
    });
    setFieldValue("categoryList", catList);
    setFieldValue("categories", newCategories);
  }

  useEffect(() => {
    const go = async () => {
      if (authContext?.authState?.identity?.isAdmin !== true) {
        navigate("/home");
        setLoading(false);
        return;
      }

      if (!productId) {
        setProduct({});
        setLoading(false);
        return;
      }
      try {
        var apiProduct = await getData(`/products/${productId}`, {
          queryStringParameters: {},
        });
        setProduct(apiProduct);
      } catch (err) {
        postNotification("error", err);

        setLoading(false);
        return;
      }
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, [productId]);

  return (
    <>
      {!loading && (
        <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
          <Formik
            initialValues={{
              active: product?.active !== undefined ? product.active : false,
              featured:
                product?.featured !== undefined ? product.featured : false,
              name: product?.name || "",
              sku: product?.sku || "",
              description: product?.description || "",
              categoryList:
                product?.categories?.length > 0
                  ? product.categories.join(",")
                  : "",
              categories:
                product?.categories?.length > 0 ? product.categories : [],
              qtyOnHand: product?.qtyOnHand || 0,
              loyaltyPoints:
                product?.loyaltyPoints ||
                (getEnvironmentVariable("SUPPORTS_LOYALTY_STORE") === "true"
                  ? ""
                  : 0),
              imageFiles: product?.imageFiles || [],
              externalLinks: product?.externalLinks || [],
            }}
            validationSchema={Yup.object().shape({
              name: Yup.string()
                .min(3, "Minimum 3 Characters!")
                .max(255, "Maximum 255 Characters!")
                .required("Required"),
              qtyOnHand: Yup.number().min(0).required("Required"),
              loyaltyPoints:
                getEnvironmentVariable("SUPPORTS_LOYALTY_STORE") === "true"
                  ? Yup.number().min(0).required("Required")
                  : null,
            })}
            onSubmit={async (values) => {
              if (!values.categories?.length) {
                postNotification("error", "Please enter at least one category");
                return;
              }
              setProcessing(true);
              try {
                var body;
                var sanitizedValues = {
                  ...values,
                };

                if (sanitizedValues.imageFile?.imageURL) {
                  // upload image to s3
                  var newFile = await uploadBas64ToS3(
                    sanitizedValues.imageFile.imageURL
                  );
                  delete sanitizedValues.imageFile;
                  if (newFile.fileId) {
                    sanitizedValues.imageFileId = newFile.fileId;
                  }
                }

                if (sanitizedValues.imageFiles.length > 0) {
                  for (var i = 0; i < sanitizedValues.imageFiles.length; i++) {
                    if (sanitizedValues.imageFiles[i].imageURL) {
                      // upload image to s3
                      var newGalleryItem = await uploadBas64ToS3(
                        sanitizedValues.imageFiles[i].imageURL
                      );
                      delete sanitizedValues.imageFiles[i].imageURL;
                      if (newGalleryItem.fileId) {
                        sanitizedValues.imageFiles[i].imageFileId =
                          newGalleryItem.fileId;
                      }
                    }
                  }
                }

                if (product?.id) {
                  body = {
                    set: sanitizedValues,
                  };

                  await postData(`/products/${product.id}`, {
                    body: body,
                  });
                  postNotification("success", "Product Updated");
                } else {
                  var apiData = await postData(`/products`, {
                    body: sanitizedValues,
                  });
                  postNotification("success", "Product Saved");
                  window.location.assign(`/admin/shop/product/${apiData.id}`);
                }
              } catch (err) {
                postNotification("error", err);
              }
              setProcessing(false);
              return;
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit} noValidate>
                <Grid container sx={{ gap: 2 }}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      p: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 1,
                      boxShadow: 1,
                      backgroundColor: "background.secondary",
                    }}
                  >
                    <Typography variant="h3">Product</Typography>
                    <Typography variant="h4">
                      {values?.name || "New Product"}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sx={{
                      p: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 1,
                      boxShadow: 1,
                      backgroundColor: "background.secondary",
                    }}
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          label="Active"
                          sx={{
                            ml: 0,
                            width: "100%",
                            "& .MuiFormControlLabel-label": {
                              width: "calc(100% - 58px)",
                            },
                          }}
                          control={
                            <Switch
                              checked={values.active}
                              onChange={(e) => {
                                setFieldValue("active", !values.active);
                              }}
                            />
                          }
                          labelPlacement="start"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          label="Featured"
                          sx={{
                            ml: 0,
                            width: "100%",
                            "& .MuiFormControlLabel-label": {
                              width: "calc(100% - 58px)",
                            },
                          }}
                          control={
                            <Switch
                              checked={values.featured}
                              onChange={(e) => {
                                setFieldValue("featured", !values.featured);
                              }}
                            />
                          }
                          labelPlacement="start"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="name"
                          label="Product Name"
                          value={values.name}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.name && errors.name)}
                          helperText={touched.name && errors.name}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          name="sku"
                          label="Product SKU"
                          value={values.sku}
                          variant="outlined"
                          fullWidth
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.sku && errors.sku)}
                          helperText={touched.sku && errors.sku}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="categoryList"
                          label="Categories"
                          value={values.categoryList}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          inputProps={{ style: { textTransform: "lowercase" } }}
                          onBlur={async (e) => {
                            handleCategoryChange(e, setFieldValue, handleBlur);
                          }}
                          onChange={(e) => {
                            handleCategoryChange(
                              e,
                              setFieldValue,
                              handleChange
                            );
                          }}
                          error={Boolean(
                            touched.categoryList && errors.categoryList
                          )}
                          helperText={
                            touched.categoryList && errors.categoryList
                          }
                        />
                        {values.categories.map((cat, index) => {
                          return (
                            <Chip
                              label={cat}
                              onDelete={(e) => {
                                handleDeleteCategory(
                                  cat,
                                  values.categories,
                                  setFieldValue
                                );
                              }}
                              color="primary"
                              variant="outlined"
                            />
                          );
                        })}
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1" gutterBottom>
                          Primary Image
                        </Typography>
                        <ImageUploader
                          values={values}
                          label={values.name}
                          name="imageFile.imageURL"
                          setFieldValue={setFieldValue}
                          fileId={product?.imageFileId}
                          defaultUrl={values.imageFile?.imageURL}
                          aspectW={1}
                          aspectY={1}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1" gutterBottom>
                          Product Gallery
                        </Typography>

                        <Grid container spacing={1}>
                          <FieldArray
                            name={`imageFiles`}
                            render={(arrayHelpers) => (
                              <>
                                {values.imageFiles?.length > 0 &&
                                  values.imageFiles.map((picture, rowIndex) => {
                                    return (
                                      <Grid item xs={12}>
                                        <ImageUploader
                                          label="Secondary Image"
                                          name={`imageFiles[${rowIndex}].imageURL`}
                                          setFieldValue={setFieldValue}
                                          fileId={
                                            values.imageFiles[rowIndex]
                                              .imageFileId || null
                                          }
                                          defaultUrl={
                                            values.imageFiles?.[rowIndex]
                                              ?.imageURL
                                          }
                                          noAspect
                                        />
                                      </Grid>
                                    );
                                  })}
                                {values.imageFiles?.length < 5 && (
                                  <Grid item xs={12}>
                                    <Button
                                      variant="outlined"
                                      color="primary"
                                      fullWidth
                                      onClick={() => arrayHelpers.push({})}
                                    >
                                      Add Image
                                    </Button>
                                  </Grid>
                                )}
                              </>
                            )}
                          />
                        </Grid>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="qtyOnHand"
                          label="Quantity On Hand"
                          value={values.qtyOnHand}
                          variant="outlined"
                          fullWidth
                          required
                          type="number"
                          inputProps={{
                            min: 0,
                          }}
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.qtyOnHand && errors.qtyOnHand)}
                          helperText={touched.qtyOnHand && errors.qtyOnHand}
                        />
                      </Grid>
                      {getEnvironmentVariable("SUPPORTS_LOYALTY_STORE") ===
                        "true" && (
                        <Grid item xs={12}>
                          <TextField
                            name="loyaltyPoints"
                            label="Loyalty Points"
                            value={values.loyaltyPoints}
                            variant="outlined"
                            fullWidth
                            required
                            type="number"
                            inputProps={{
                              min: 0,
                            }}
                            margin="normal"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.loyaltyPoints && errors.loyaltyPoints
                            )}
                            helperText={
                              touched.loyaltyPoints && errors.loyaltyPoints
                            }
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <TextField
                          name="description"
                          value={values.description}
                          label="Description"
                          variant="outlined"
                          fullWidth
                          onBlur={handleBlur}
                          onChange={handleChange}
                          multiline
                          rows={5}
                          error={Boolean(
                            touched.description && errors.description
                          )}
                          helperText={touched.description && errors.description}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1" gutterBottom>
                          External Links
                        </Typography>

                        <Grid container spacing={1}>
                          <FieldArray
                            name={`externalLinks`}
                            render={(arrayHelpers) => (
                              <>
                                {values.externalLinks?.length > 0 &&
                                  values.externalLinks.map(
                                    (externalLink, index) => {
                                      return (
                                        <Grid
                                          key={index}
                                          item
                                          xs={12}
                                          sx={{
                                            borderTop:
                                              "1px solid rgba(0, 0, 0, 0.12)",
                                            borderBottom:
                                              "1px solid rgba(0, 0, 0, 0.12)",
                                          }}
                                        >
                                          <Grid
                                            container
                                            spacing={1}
                                            sx={{ mb: 2 }}
                                            alignItems="center"
                                          >
                                            <Grid item xs={12}>
                                              <TextField
                                                name={`externalLinks[${index}].label`}
                                                variant="outlined"
                                                fullWidth
                                                margin="normal"
                                                label="Label"
                                                error={Boolean(
                                                  touched.externalLinks?.[index]
                                                    ?.label &&
                                                    errors.externalLinks?.[
                                                      index
                                                    ]?.label
                                                )}
                                                helperText={
                                                  touched.externalLinks?.[index]
                                                    ?.label &&
                                                  errors.externalLinks?.[index]
                                                    ?.label
                                                }
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={
                                                  values.externalLinks[index]
                                                    .label
                                                }
                                              />
                                            </Grid>
                                            <Grid item xs={12}>
                                              <TextField
                                                name={`externalLinks[${index}].href`}
                                                variant="outlined"
                                                fullWidth
                                                margin="normal"
                                                label="URL"
                                                error={Boolean(
                                                  touched.externalLinks?.[index]
                                                    ?.href &&
                                                    errors.externalLinks?.[
                                                      index
                                                    ]?.href
                                                )}
                                                helperText={
                                                  touched.externalLinks?.[index]
                                                    ?.href &&
                                                  errors.externalLinks?.[index]
                                                    ?.href
                                                }
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={
                                                  values.externalLinks[index]
                                                    .href
                                                }
                                              />
                                            </Grid>

                                            <Grid
                                              item
                                              xs={12}
                                              textAlign="right"
                                            >
                                              <Button
                                                onClick={(e) => {
                                                  arrayHelpers.remove(index);
                                                }}
                                                type="button"
                                                color="error"
                                                variant="contained"
                                              >
                                                Delete
                                              </Button>
                                            </Grid>
                                          </Grid>
                                        </Grid>
                                      );
                                    }
                                  )}

                                <Grid item xs={12}>
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    fullWidth
                                    onClick={() =>
                                      arrayHelpers.push({ label: "", href: "" })
                                    }
                                  >
                                    Add External Link
                                  </Button>
                                </Grid>
                              </>
                            )}
                          />
                        </Grid>
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          ref={saveBoxRef}
                          fullWidth
                          color="success"
                          variant="contained"
                          type="submit"
                        >
                          Save
                        </Button>
                      </Grid>

                      {!saveBoxInView && (
                        <Box
                          sx={{
                            position: "fixed",
                            zIndex: 999,
                            p: 2,
                            bgcolor: "background.paper",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                            boxShadow: 3,
                            bottom: 0,
                            left: [0, 0, 0, 256],
                            width: [
                              "100%",
                              "100%",
                              "100%",
                              "calc(100% - 256px)",
                            ],
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Button
                              fullWidth
                              color="success"
                              variant="contained"
                              type="submit"
                            >
                              Save
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Container>
      )}
      {(loading || processing) && <ModalLoadingFull />}
    </>
  );
}
